import { useState, useRef, useCallback, useMemo } from 'react';
import { Stack, useCoralTheme } from '@krakentech/coral';
import { motion, AnimatePresence } from 'framer-motion';

import {
  CONTAINER_ANIMATION,
  SPACING_ANIMATION,
  SUBTITLE_ANIMATION,
  WHEEL_BUTTON_ANIMATIONS,
  WHEEL_ROTATION_ANIMATION,
  POINTER_ANIMATION,
} from '@/components/WheelOfFortune/WheelOfFortune/animations';
import Segment from '@/components/WheelOfFortune/WheelOfFortune/components/Segment';
import {
  WHEEL_SEGMENTS,
  VISUAL_PRIZE_AMOUNTS,
} from '@/components/WheelOfFortune/WheelOfFortune/constants';
import {
  StyledWheelOfFortune,
  WheelButton,
  WheelSVG,
  Pointer,
} from '@/components/WheelOfFortune/WheelOfFortune/styles';
import { createSegments } from '@/components/WheelOfFortune/WheelOfFortune/utils';
import { RewardBrand } from '@/types/rewards';

type WheelOfFortuneProps = {
  onSpinComplete?: (prize: RewardBrand) => void;
  isSpinning: boolean;
  onSpin: () => void;
};

const WheelOfFortune = ({
  onSpinComplete,
  isSpinning,
  onSpin,
}: WheelOfFortuneProps) => {
  const [rotation, setRotation] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const wheelRef = useRef<SVGSVGElement>(null);
  const { color } = useCoralTheme();

  const handleSpin = useCallback(() => {
    if (isSpinning || isAnimating) return;

    setIsAnimating(true);
    onSpin();

    // Fixed rotation - just spin 6 full rotations
    const finalRotation = -(6 * 360);

    setRotation(finalRotation);

    // Simulate spin duration
    setTimeout(() => {
      setIsAnimating(false);
      onSpinComplete?.(RewardBrand.ACCOUNT_CREDIT); // Fixed prize since it's hardcoded for now
    }, 3000);
  }, [isSpinning, isAnimating, onSpin, onSpinComplete]);

  const segments = useMemo(() => {
    return createSegments(color, VISUAL_PRIZE_AMOUNTS, WHEEL_SEGMENTS);
  }, [color]);

  return (
    <motion.div {...CONTAINER_ANIMATION}>
      <motion.div {...SPACING_ANIMATION}></motion.div>
      <motion.div {...SUBTITLE_ANIMATION}></motion.div>

      <Stack>
        <WheelButton
          onClick={handleSpin}
          disabled={isSpinning || isAnimating}
          aria-label="Spin the wheel of fortune"
          {...WHEEL_BUTTON_ANIMATIONS}
        >
          <StyledWheelOfFortune
            animate={WHEEL_ROTATION_ANIMATION.animate(rotation)}
            transition={WHEEL_ROTATION_ANIMATION.transition(isAnimating)}
            whileHover={WHEEL_ROTATION_ANIMATION.whileHover(isAnimating)}
          >
            <WheelSVG ref={wheelRef} viewBox="-200 -200 400 400">
              <AnimatePresence>
                {segments.map((segment, index) => (
                  <Segment
                    key={segment.id}
                    id={segment.id}
                    index={index}
                    color={segment.colour}
                    prize={segment.prize}
                  />
                ))}
              </AnimatePresence>
            </WheelSVG>
          </StyledWheelOfFortune>
          <Pointer
            animate={POINTER_ANIMATION.animate(isAnimating)}
            transition={{
              ...POINTER_ANIMATION.transition,
              repeat: POINTER_ANIMATION.transition.repeat(isAnimating),
            }}
          />
        </WheelButton>
      </Stack>
    </motion.div>
  );
};

export default WheelOfFortune;
